import type { CalendarEvent } from "../types";

// ---------------------------------------------------------------------------
// Grid geometry (mirrors the prototype `Class Scheduler.dc.html` lines 1174–1258).
// 1 px per minute, 8 AM–4 PM window, 56 px left gutter for hour labels.
// ---------------------------------------------------------------------------
export const GRID_GUTTER = 56;
export const GRID_T0_MIN = 480; // 8 AM expressed as minutes since local midnight
export const GRID_PXM = 1;
export const GRID_PAD = 10;
export const GRID_HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16] as const;
export const GRID_HOUR_LABELS = [
  "8 AM",
  "9 AM",
  "10 AM",
  "11 AM",
  "12 PM",
  "1 PM",
  "2 PM",
  "3 PM",
  "4 PM",
] as const;

// Total grid body height in px: from 8 AM (`GRID_T0_MIN`) through 4 PM
// (`16 * 60` minutes) plus top padding and a tiny bottom inset so the last
// half-hour line clears the bottom border.
export const GRID_BODY_HEIGHT =
  (16 * 60 - GRID_T0_MIN) * GRID_PXM + GRID_PAD + 6;

const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const satisfies readonly [
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
];

// Default "demo" week used by the prototype: Mon Sep 7, 2026.
const DEFAULT_DEMO_MONDAY = new Date(2026, 8, 7); // months are 0-indexed

/** Type returned by {@link assignLanes}; adds lane assignment in-place. */
export interface LaidOutEvent extends CalendarEvent {
  /** Lane index within this event's overlap cluster (0-based). */
  lane: number;
  /** Total lane count for this event's overlap cluster (>= 1). */
  lanes: number;
  /** Start time as minutes since local midnight (for the day this event was placed on). */
  startMin: number;
  /** End time as minutes since local midnight. */
  endMin: number;
}

/**
 * Internal cluster entry — every event is initialized with `lane = -1` so the
 * narrowing at `flushCluster` time does not require an unsafe cast.
 */
type ClusterEntry = CalendarEvent & {
  startMin: number;
  endMin: number;
  lane: number;
};

/**
 * Run the prototype's overlap-lane sweep on a set of events. Events must
 * already be filtered to a single day and carry `startMin`/`endMin` fields.
 * Sorted by start ascending, ties broken by end ascending.
 *
 * The algorithm groups events into clusters by transitive overlap, then within
 * each cluster places each event in the lowest-index lane that is free at the
 * event's start. All events in the cluster get the same `lanes` count so they
 * lay out side-by-side without overlap.
 *
 * @param events events for a single day, with start/end in local minutes.
 * @returns events annotated with `lane` and `lanes`.
 */
export function assignLanes(
  events: Array<CalendarEvent & { startMin: number; endMin: number }>,
): LaidOutEvent[] {
  if (events.length === 0) return [];

  const sorted = [...events].sort(
    (a, b) => a.startMin - b.startMin || a.endMin - b.endMin,
  );

  const out: LaidOutEvent[] = [];
  let cluster: ClusterEntry[] = [];
  let clusterEnd = -1;

  const flushCluster = () => {
    const colEnds: number[] = [];
    for (const ev of cluster) {
      let placed = false;
      for (let c = 0; c < colEnds.length; c++) {
        const colEnd = colEnds[c];
        if (colEnd !== undefined && ev.startMin >= colEnd) {
          ev.lane = c;
          colEnds[c] = ev.endMin;
          placed = true;
          break;
        }
      }
      if (!placed) {
        ev.lane = colEnds.length;
        colEnds.push(ev.endMin);
      }
    }
    const lanes = colEnds.length;
    for (const ev of cluster) {
      out.push({ ...ev, lanes });
    }
    cluster = [];
    clusterEnd = -1;
  };

  for (const ev of sorted) {
    // If this event starts at or after the cluster's max end, it doesn't
    // transitively overlap anything in the current cluster — close it out.
    if (cluster.length > 0 && ev.startMin >= clusterEnd) {
      flushCluster();
    }
    cluster.push({ ...ev, lane: -1 });
    clusterEnd = Math.max(clusterEnd, ev.endMin);
  }
  if (cluster.length > 0) flushCluster();

  return out;
}

/** Fixed-length tuple of the five weekday Dates (Mon..Fri). */
export type WeekDays = readonly [Date, Date, Date, Date, Date];

/**
 * Return the five weekday Dates (Mon..Fri) starting at the given Monday. The
 * input is treated as a calendar date — only the date portion matters; the
 * caller is responsible for picking a stable time-of-day (typically local
 * midnight).
 *
 * The return type is a fixed-length tuple so index access is `Date`, not
 * `Date | undefined` under `noUncheckedIndexedAccess`.
 */
export function getWeekDays(weekStart: Date): WeekDays {
  const mk = (offset: number) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + offset);
    return d;
  };
  return [mk(0), mk(1), mk(2), mk(3), mk(4)] as const;
}

/**
 * Compute the Monday of the week containing `now` in the user's local time.
 * For the demo phase we fall back to Mon 2026-09-07 if `now` is before
 * 2026-08-01 so the seed/demo data shows up on first load.
 */
export function getCurrentWeekStart(now: Date): Date {
  if (now.getTime() < new Date(2026, 7, 1).getTime()) {
    return new Date(DEFAULT_DEMO_MONDAY);
  }
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  const dow = d.getDay(); // 0 = Sun, 1 = Mon, ... 6 = Sat
  // Distance back to Monday: Sunday -> 6, Monday -> 0, Tue -> 1, ...
  const diff = (dow + 6) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

/** Short month name (`Jan`, `Feb`, ...) for a 0-indexed month (0-11). */
function shortMonth(monthIndex: number): string {
  // Clamp so the tuple lookup is always safe — JS `Date#getMonth` already
  // returns 0–11 but the explicit fallback keeps the function total.
  const clamped = Math.min(11, Math.max(0, monthIndex));
  // `as 0|1|...|11` would also work; cast through the tuple keeps it readable.
  return SHORT_MONTHS[
    clamped as 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11
  ];
}

/**
 * Format a week range like `Sep 7 – 11, 2026` (en-dash). Collapses the
 * second month when both ends fall in the same month — otherwise it expands
 * to `Aug 31 – Sep 4, 2026`.
 */
export function formatWeekLabel(weekStart: Date, weekEnd: Date): string {
  const startMonth = shortMonth(weekStart.getMonth());
  const startDay = weekStart.getDate();
  const endMonth = shortMonth(weekEnd.getMonth());
  const endDay = weekEnd.getDate();
  const year = weekEnd.getFullYear();

  if (weekStart.getMonth() === weekEnd.getMonth()) {
    return `${startMonth} ${startDay} – ${endDay}, ${year}`;
  }
  return `${startMonth} ${startDay} – ${endMonth} ${endDay}, ${year}`;
}

// ---------------------------------------------------------------------------
// Timezone helpers.
//
// The backend stores and returns event timestamps as UTC ISO strings. To draw
// them on a weekday column we need to know "what local wall-clock time does
// this UTC instant correspond to in the schedule's timezone". We do that with
// `Intl.DateTimeFormat` (no new dependencies).
// ---------------------------------------------------------------------------

interface TzParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number; // 0-23
  minute: number;
  second: number;
}

const tzFormatters = new Map<string, Intl.DateTimeFormat>();

function getTzFormatter(tzId: string): Intl.DateTimeFormat {
  let f = tzFormatters.get(tzId);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone: tzId,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    tzFormatters.set(tzId, f);
  }
  return f;
}

/** Convert a UTC `Date` to wall-clock parts in the given IANA timezone. */
function utcToZonedParts(date: Date, tzId: string): TzParts {
  const parts = getTzFormatter(tzId).formatToParts(date);
  const lookup: Record<string, string> = {};
  for (const p of parts) {
    if (p.type !== "literal") lookup[p.type] = p.value;
  }
  // `Intl` returns `24` for midnight under `hour12: false` in some engines.
  let hour = Number(lookup.hour);
  if (hour === 24) hour = 0;
  return {
    year: Number(lookup.year),
    month: Number(lookup.month),
    day: Number(lookup.day),
    hour,
    minute: Number(lookup.minute),
    second: Number(lookup.second),
  };
}

/**
 * Convert a {@link CalendarEvent} (which carries UTC ISO strings) into the
 * "minutes since local midnight" window for a specific calendar day in the
 * schedule's timezone.
 *
 * @param event the event to project.
 * @param dayDate any `Date` whose local *calendar date* (in `tzId`) is the
 *   target day; we extract the year/month/day in `tzId`. Time-of-day is
 *   ignored.
 * @param tzId IANA timezone of the schedule.
 * @returns `{ startMin, endMin }` clamped to `[0, 24 * 60]` — or `null` if the
 *   event does not intersect the target day at all in `tzId`.
 */
export function eventToZonedMinutes(
  event: CalendarEvent,
  dayDate: Date,
  tzId: string,
): { startMin: number; endMin: number } | null {
  const start = new Date(event.dtStartUtc);
  const end = new Date(event.dtEndUtc);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;

  const targetParts = utcToZonedParts(dayDate, tzId);

  const startParts = utcToZonedParts(start, tzId);
  const endParts = utcToZonedParts(end, tzId);

  const sameDay = (p: TzParts) =>
    p.year === targetParts.year &&
    p.month === targetParts.month &&
    p.day === targetParts.day;

  const startSame = sameDay(startParts);
  const endSame = sameDay(endParts);

  // If neither end of the event falls on the target day, check whether the
  // event straddles the day entirely (start before, end after). We compare
  // by `Y/M/D` lexicographic ordering — cheap and tz-correct.
  const cmpYMD = (a: TzParts, b: TzParts) => {
    if (a.year !== b.year) return a.year - b.year;
    if (a.month !== b.month) return a.month - b.month;
    return a.day - b.day;
  };

  const startCmp = cmpYMD(startParts, targetParts);
  const endCmp = cmpYMD(endParts, targetParts);

  if (!startSame && !endSame && !(startCmp < 0 && endCmp > 0)) {
    return null;
  }

  const startMin = startSame ? startParts.hour * 60 + startParts.minute : 0;
  const endMin = endSame ? endParts.hour * 60 + endParts.minute : 24 * 60;

  // Guard against zero/negative — a same-day event with end <= start (rare,
  // typically malformed) gets a 1-minute minimum so it still renders.
  if (endMin <= startMin) return { startMin, endMin: startMin + 1 };

  return { startMin, endMin };
}

/**
 * Compute the UTC instant of local midnight on `year/month/day` in `tzId`.
 *
 * Uses a two-pass approach: first guess by treating the parts as UTC, then
 * read back the resulting tz wall-clock and apply the delta. One pass would be
 * wrong across DST boundaries; two passes are enough because every IANA zone's
 * UTC offset is constant within any given calendar day.
 *
 * @param year four-digit year
 * @param month 1-indexed month (1-12)
 * @param day day of month (1-31)
 * @param tzId IANA timezone id
 * @returns the UTC `Date` corresponding to that wall-clock midnight in `tzId`.
 */
export function zonedDateToUtc(
  year: number,
  month: number,
  day: number,
  tzId: string,
): Date {
  // Guess: pretend the wall-clock components ARE UTC. This is off by exactly
  // the zone's UTC offset.
  const guess = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
  const guessParts = utcToZonedParts(guess, tzId);

  // What wall-clock did the guess produce in `tzId`? Compare to what we
  // wanted and adjust by the delta in minutes.
  const wantedAsMs = Date.UTC(year, month - 1, day, 0, 0, 0);
  const gotAsMs = Date.UTC(
    guessParts.year,
    guessParts.month - 1,
    guessParts.day,
    guessParts.hour,
    guessParts.minute,
    guessParts.second,
  );
  const deltaMs = wantedAsMs - gotAsMs;
  return new Date(guess.getTime() + deltaMs);
}
